import { useState } from 'react';
import { Flex, useMediaQuery } from '@chakra-ui/react';
import RatePlanDetails from '@view/components/Modals/RatePlanDetails';
import GuestDropdown from '@view/components/Popovers/GuestDropdown';
import Button from '@molecules/Button';
import Stepper from '@molecules/Stepper';
import Text from '@molecules/Text';
import BEIcon from '@molecules/BEIcon';
import { useModal } from '@molecules/BEOffScreen';
import SelectIndividual from '@view/components/Modals/SelectIndividual';
import {
  StyledPlanCardContainer,
  StyledPlanInfoContainer,
  StyledButton,
} from './StyledRatePlanCard';
import ScreenSize from '../../../../constants/screen-sizes';
import Accommodation from '@data/models/Accommodation';
import { useStore } from '@data/other/withStore';
import useFormat from '@business/utils/hooks/useFormat';
import { observer } from 'mobx-react';
import BEImage from '@molecules/BEImage';

/**
 * There are different types of roomDetailCard
 * @param isPrivate: private or shared room
 * @param isAllowed: booking individual units is allowed or not
 */

const RatePlanCard = (props: Accommodation & { showThumb?: boolean }) => {
  const {
    property: { cart, accommodations },
    components,
  } = useStore();
  const {
    images,
    title,
    policy,
    maxGuests,
    maxRooms,
    available,
    notDerivedRate,
    rate,
    nights,
    isPrivate,
    sourceSettings,
    packageId,
    packageDescription,
    packageTerms,
  } = props;
  const currencyRate = accommodations.model?.currency_rate || 1;

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const [rooms, setRooms] = useState<number>(1);
  const [adults, setAdults] = useState<number>(Math.min(2, maxGuests));
  const [kids, setKids] = useState<number>(0);

  const { currency } = useFormat();

  const onClickAdd = () => {
    cart.addAccommodation(props, rooms, adults, kids);
  };

  function onChangeGuests(adults: number, kids: number) {
    setAdults(adults);
    setKids(kids);
  }

  const selectIndividualModal = useModal(SelectIndividual, {});

  function onClickSelect() {
    const { checkin, checkout } = components.promoCode.value;
    selectIndividualModal.open({
      accommodation: props,
      checkin: checkin as Date,
      checkout: checkout as Date,
    });
  }

  const enableModal =
    packageId !== '0' && (packageDescription !== '' || packageTerms !== '');

  const ratePlanDetailsModal = useModal(RatePlanDetails, {});
  const handleClickTitle = () => {
    ratePlanDetailsModal.open({ ...props });
  };

  function getNightsText(nights: number) {
    return nights === 1 ? `${nights} Night` : `${nights} Nights`;
  }

  return (
    <StyledPlanCardContainer>
      {!isMobile && props.showThumb && (
        <BEImage
          width="200px"
          height="130px"
          src={images[0].thumb}
          borderRadius="8px"
        />
      )}
      <StyledPlanInfoContainer>
        <Flex justifyContent="space-between" gap={'8px'}>
          <Flex
            direction="column"
            gap={isMobile ? '0' : '4px'}
            justifyContent="flex-start"
            alignItems="flex-start"
            flex={1}
          >
            {enableModal ? (
              <Button
                className="text-underline"
                variant="link"
                size="md"
                weight="600"
                color="black"
                onClick={handleClickTitle}
              >
                {title}
              </Button>
            ) : (
              <Text type="h3">{title}</Text>
            )}
            {policy.length > 0 && (
              <Flex gap="2px">
                {!isMobile && <BEIcon name="calendar-check" />}
                <Text size="md" mobilesize="sm">
                  {policy}
                </Text>
              </Flex>
            )}
          </Flex>
          <Flex direction="column" alignItems="flex-end">
            <Flex alignItems="center" gap="4px">
              {notDerivedRate > 0 && notDerivedRate !== rate && (
                <Text
                  className="text-decoration"
                  color="error"
                  darkness="400"
                  size="sm"
                  mobilesize="sm"
                >
                  {currency(notDerivedRate * currencyRate)}
                </Text>
              )}
              <Text type="h3">{currency(rate * currencyRate)}</Text>
            </Flex>
            <Text
              type="body"
              color="neutral"
              darkness="700"
              size="sm"
              mobilesize="sm"
            >
              {getNightsText(nights)}
            </Text>
            {!isMobile && sourceSettings.canSelectSpecificRoom && (
              <StyledButton
                className="book-button"
                color="black"
                size="md"
                variant="outline"
                onClick={onClickSelect}
              >
                {`Select ${isPrivate ? 'Accommodations' : 'Beds'}`}
              </StyledButton>
            )}
          </Flex>
        </Flex>
        {isMobile && sourceSettings.canSelectSpecificRoom && (
          <Flex justifyContent="flex-end">
            <StyledButton
              className="book-button"
              color="black"
              size="md"
              variant="outline"
              onClick={onClickSelect}
            >
              {`Select ${isPrivate ? 'Accommodations' : 'Beds'}`}
            </StyledButton>
          </Flex>
        )}
        {!sourceSettings.canSelectSpecificRoom && (
          <Flex gap="16px" justifyContent="flex-end" alignItems="end">
            {isPrivate ? (
              <>
                <GuestDropdown
                  label="Guests"
                  adults={adults}
                  kids={0}
                  maxOccupancy={maxGuests}
                  size="md"
                  onChange={onChangeGuests}
                />
                <Stepper
                  size="md"
                  label="Rooms"
                  value={rooms}
                  minValue={1}
                  maxValue={Math.min(available, maxRooms)}
                  typedInputDisabled={false}
                  onChange={setRooms}
                />
              </>
            ) : (
              <Stepper
                size="md"
                label="Beds"
                value={rooms}
                minValue={1}
                maxValue={Math.min(available, maxRooms)}
                onChange={setRooms}
              />
            )}
            <Button
              className="book-button"
              color="primary"
              size="md"
              onClick={onClickAdd}
            >
              Add
            </Button>
          </Flex>
        )}
      </StyledPlanInfoContainer>
    </StyledPlanCardContainer>
  );
};

export default observer(RatePlanCard);
